import React, { useState, useEffect, useRef } from 'react';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import { useMarket } from '../context/MarketContext';
import FormPanel from './FormPanel';
import { trackMenuOpen, trackFormSubmit } from '../utils/analytics';
import './HamburgerMenu.css';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Menu item configuration.
 * action: 'navigate' = go to a route, 'form' = open a FormPanel
 */
const MENU_ITEMS = [
  {
    id: 'sign-in',
    icon: '🔑',
    title: 'Sign In',
    subtitle: 'For venue owners & admins',
    action: 'navigate',
    navigateTo: '/login',
  },
  {
    id: 'submit-spot',
    icon: '📍',
    title: 'Submit a Spot',
    subtitle: 'Know a great spot? Let us know!',
    action: 'form',
    formId: 'submit-spot',
  },
  {
    id: 'claim-venue',
    icon: '🏪',
    title: 'Claim Your Venue',
    subtitle: 'Manage your business listing',
    action: 'form',
    formId: 'claim-venue',
  },
  {
    id: 'advertise',
    icon: '📢',
    title: 'Advertise With Us',
    subtitle: 'Promote your business',
    action: 'form',
    formId: 'advertise',
  },
  {
    id: 'contact',
    icon: '✉️',
    title: 'Contact Us',
    subtitle: 'Questions? Reach out!',
    action: 'form',
    formId: 'contact',
  },
];

// Default form field values
const EMPTY_SUBMIT_FORM = { venue_name: '', venue_city: '', name: '', email: '', message: '', is_owner: false };
const EMPTY_ADVERTISE_FORM = { business_name: '', name: '', email: '', message: '' };
const EMPTY_CONTACT_FORM = { name: '', email: '', message: '' };
const EMPTY_CLAIM_FORM = { name: '', email: '', venue_name: '', venue_city: '', message: '' };

/**
 * HamburgerMenu — self-contained slide-out navigation drawer + form panels.
 * Renders a trigger button (three bars), a navigation drawer, and form panels.
 * OTC markets only — returns null for markets without hamburgerMenu feature.
 */
const HamburgerMenu = () => {
  const market = useMarket();
  const navigate = useMarketNavigate();

  // Drawer state
  const [isOpen, setIsOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimerRef = useRef(null);
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);

  // Form panel state
  const [activePanel, setActivePanel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Form field state
  const [submitForm, setSubmitForm] = useState(EMPTY_SUBMIT_FORM);
  const [advertiseForm, setAdvertiseForm] = useState(EMPTY_ADVERTISE_FORM);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [claimForm, setClaimForm] = useState(EMPTY_CLAIM_FORM);

  // Claim form restaurant dropdown state
  const [claimRestaurants, setClaimRestaurants] = useState([]);
  const [claimRestaurantsLoaded, setClaimRestaurantsLoaded] = useState(false);
  const [venueNotListed, setVenueNotListed] = useState(false);

  // Gate: only render for markets with hamburgerMenu feature enabled
  if (!market.features?.hamburgerMenu) {
    return null;
  }

  // ---- Drawer Handlers ----

  const handleOpen = () => {
    trackMenuOpen();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => triggerRef.current?.focus(), 100);
  };

  const showToast = (message) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
      toastTimerRef.current = null;
    }, 2500);
  };

  const fetchClaimRestaurants = async () => {
    if (claimRestaurantsLoaded) return;
    try {
      const res = await fetch(`${API_URL}/api/restaurants/${market.airtableBaseId}/names`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setClaimRestaurants(data.restaurants || []);
    } catch {
      setClaimRestaurants([]);
    } finally {
      setClaimRestaurantsLoaded(true);
    }
  };

  const handleMenuItemClick = (item) => {
    if (item.action === 'navigate') {
      handleClose();
      navigate(item.navigateTo);
    } else if (item.action === 'form') {
      handleClose();
      // Prefetch restaurant list for claim form
      if (item.formId === 'claim-venue') {
        fetchClaimRestaurants();
      }
      // Small delay so the menu drawer finishes closing before form opens
      setTimeout(() => {
        setActivePanel(item.formId);
        setSubmitError(null);
        setSubmitSuccess(false);
        setFieldErrors({});
      }, 150);
    }
  };

  // ---- Form Handlers ----

  const closeForm = () => {
    if (submitting) return;
    setActivePanel(null);
    setSubmitError(null);
    setSubmitSuccess(false);
    setFieldErrors({});
    setVenueNotListed(false);
    // Reset form data
    setSubmitForm(EMPTY_SUBMIT_FORM);
    setAdvertiseForm(EMPTY_ADVERTISE_FORM);
    setContactForm(EMPTY_CONTACT_FORM);
    setClaimForm(EMPTY_CLAIM_FORM);
  };

  const validateForm = (type, data) => {
    const errs = {};
    if (type === 'venue_submission') {
      if (!data.venue_name?.trim()) errs.venue_name = 'Venue name is required';
      if (!data.venue_city?.trim()) errs.venue_city = 'City is required';
    } else if (type === 'ad_inquiry') {
      if (!data.business_name?.trim()) errs.business_name = 'Business name is required';
      if (!data.name?.trim()) errs.name = 'Name is required';
      if (!data.email?.trim()) errs.email = 'Email is required';
    } else if (type === 'contact') {
      if (!data.name?.trim()) errs.name = 'Name is required';
      if (!data.email?.trim()) errs.email = 'Email is required';
      if (!data.message?.trim()) errs.message = 'Message is required';
    } else if (type === 'venue_claim') {
      if (!data.name?.trim()) errs.name = 'Name is required';
      if (!data.email?.trim()) errs.email = 'Email is required';
      if (!data.venue_name?.trim()) errs.venue_name = 'Venue name is required';
      if (!data.venue_city?.trim()) errs.venue_city = 'City is required';
    }
    return errs;
  };

  const handleSubmit = async (type, formData) => {
    // Validate
    const errs = validateForm(type, formData);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSubmitError(null);
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseId: market.airtableBaseId,
          type,
          ...formData,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed');
      }
      trackFormSubmit(type);
      setSubmitSuccess(true);
      // Auto-close after 2 seconds
      setTimeout(() => {
        closeForm();
        showToast('Submitted successfully!');
      }, 2000);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Field update helpers ----

  const updateSubmitField = (field, value) => {
    setSubmitForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const updateAdvertiseField = (field, value) => {
    setAdvertiseForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const updateContactField = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const updateClaimField = (field, value) => {
    setClaimForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  // ---- Render helpers ----

  const renderField = (label, name, value, onChange, options = {}) => {
    const { required, type = 'text', placeholder, rows } = options;
    const hasError = !!fieldErrors[name];
    const InputTag = rows ? 'textarea' : (options.select ? 'select' : 'input');
    const inputClass = rows ? 'fp-textarea' : (options.select ? 'fp-select' : 'fp-input');

    return (
      <div className="fp-field" key={name}>
        <label className={`fp-label${required ? ' fp-required' : ''}`}>{label}</label>
        {options.select ? (
          <select
            className={`${inputClass}${hasError ? ' fp-input-error' : ''}`}
            value={value}
            onChange={(e) => onChange(name, e.target.value)}
          >
            <option value="">{placeholder || 'Select...'}</option>
            {options.selectOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <InputTag
            className={`${inputClass}${hasError ? ' fp-input-error' : ''}`}
            type={type}
            value={value}
            onChange={(e) => onChange(name, e.target.value)}
            placeholder={placeholder}
            rows={rows}
          />
        )}
        {hasError && <span className="fp-field-error-text">{fieldErrors[name]}</span>}
      </div>
    );
  };

  return (
    <>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        className="hm-trigger"
        onClick={handleOpen}
        aria-label="Open menu"
        aria-expanded={isOpen}
        aria-controls="hm-drawer"
      >
        <span className="hm-trigger-bar" />
        <span className="hm-trigger-bar" />
        <span className="hm-trigger-bar" />
      </button>

      {/* Drawer overlay */}
      {isOpen && (
        <HamburgerDrawer
          drawerRef={drawerRef}
          market={market}
          onClose={handleClose}
          onItemClick={handleMenuItemClick}
        />
      )}

      {/* Form Panels */}
      {activePanel === 'submit-spot' && (
        <FormPanel
          isOpen
          title="Submit a Spot"
          onClose={closeForm}
          submitting={submitting}
          success={submitSuccess}
          error={submitError}
          onSubmit={() => handleSubmit('venue_submission', submitForm)}
        >
          {renderField('Venue Name', 'venue_name', submitForm.venue_name, updateSubmitField, { required: true, placeholder: 'e.g. The Corner Pub' })}
          {renderField('City', 'venue_city', submitForm.venue_city, updateSubmitField, { required: true, select: true, selectOptions: market.cities, placeholder: 'Select city' })}
          {renderField('Your Name', 'name', submitForm.name, updateSubmitField, { placeholder: 'Optional' })}
          {renderField('Your Email', 'email', submitForm.email, updateSubmitField, { type: 'email', placeholder: 'Optional' })}
          {renderField('Why you love it', 'message', submitForm.message, updateSubmitField, { rows: 3, placeholder: 'Tell us what makes this place great (optional)' })}
          <div className="fp-checkbox-row">
            <input
              type="checkbox"
              id="is-owner"
              className="fp-checkbox"
              checked={submitForm.is_owner}
              onChange={(e) => updateSubmitField('is_owner', e.target.checked)}
            />
            <label htmlFor="is-owner" className="fp-checkbox-label">I own/manage this business</label>
          </div>
        </FormPanel>
      )}

      {activePanel === 'advertise' && (
        <FormPanel
          isOpen
          title="Advertise With Us"
          onClose={closeForm}
          submitting={submitting}
          success={submitSuccess}
          error={submitError}
          onSubmit={() => handleSubmit('ad_inquiry', advertiseForm)}
        >
          {renderField('Business Name', 'business_name', advertiseForm.business_name, updateAdvertiseField, { required: true, placeholder: 'Your business name' })}
          {renderField('Your Name', 'name', advertiseForm.name, updateAdvertiseField, { required: true, placeholder: 'Your name' })}
          {renderField('Email', 'email', advertiseForm.email, updateAdvertiseField, { required: true, type: 'email', placeholder: 'you@example.com' })}
          {renderField('Tell us about your business', 'message', advertiseForm.message, updateAdvertiseField, { required: true, rows: 4, placeholder: 'What are you looking to promote?' })}
        </FormPanel>
      )}

      {activePanel === 'contact' && (
        <FormPanel
          isOpen
          title="Contact Us"
          onClose={closeForm}
          submitting={submitting}
          success={submitSuccess}
          error={submitError}
          onSubmit={() => handleSubmit('contact', contactForm)}
        >
          {renderField('Your Name', 'name', contactForm.name, updateContactField, { required: true, placeholder: 'Your name' })}
          {renderField('Email', 'email', contactForm.email, updateContactField, { required: true, type: 'email', placeholder: 'you@example.com' })}
          {renderField('Message', 'message', contactForm.message, updateContactField, { required: true, rows: 5, placeholder: 'How can we help?' })}
        </FormPanel>
      )}

      {activePanel === 'claim-venue' && (
        <FormPanel
          isOpen
          title="Claim Your Venue"
          onClose={closeForm}
          submitting={submitting}
          success={submitSuccess}
          error={submitError}
          onSubmit={() => handleSubmit('venue_claim', claimForm)}
        >
          {renderField('Your Name', 'name', claimForm.name, updateClaimField, { required: true, placeholder: 'Your name' })}
          {renderField('Email', 'email', claimForm.email, updateClaimField, { required: true, type: 'email', placeholder: 'you@example.com' })}
          {venueNotListed ? (
            <>
              {renderField('Venue Name', 'venue_name', claimForm.venue_name, updateClaimField, { required: true, placeholder: 'Type your venue name' })}
              <div className="fp-field-hint">
                <button type="button" className="fp-link-btn" onClick={() => { setVenueNotListed(false); updateClaimField('venue_name', ''); updateClaimField('venue_city', ''); }}>
                  Back to venue list
                </button>
              </div>
              {renderField('City', 'venue_city', claimForm.venue_city, updateClaimField, { required: true, select: true, selectOptions: market.cities || [], placeholder: 'Select city' })}
            </>
          ) : (
            <>
              {renderField('Venue Name', 'venue_name', claimForm.venue_name, (field, value) => {
                updateClaimField(field, value);
                // Auto-fill city from restaurant data
                const match = claimRestaurants.find(r => r.name === value);
                if (match?.city) {
                  updateClaimField('venue_city', match.city);
                }
              }, {
                required: true,
                select: true,
                selectOptions: claimRestaurants.map(r => r.name),
                placeholder: claimRestaurantsLoaded ? 'Select your venue' : 'Loading venues...',
              })}
              <div className="fp-field-hint">
                Don't see your venue?{' '}
                <button type="button" className="fp-link-btn" onClick={() => { setVenueNotListed(true); updateClaimField('venue_name', ''); updateClaimField('venue_city', ''); }}>
                  Type it in instead
                </button>
              </div>
            </>
          )}
          {renderField('Tell us about your role', 'message', claimForm.message, updateClaimField, { rows: 3, placeholder: 'e.g. Owner, Manager, Marketing...' })}
        </FormPanel>
      )}

      {/* Toast notification */}
      {toastMessage && (
        <div className="hm-toast" role="status" aria-live="polite">
          {toastMessage}
        </div>
      )}
    </>
  );
};

/**
 * HamburgerDrawer — the actual slide-out panel.
 * Extracted as a separate component so hooks (useEffect) can run
 * without being behind the early-return gate in the parent.
 */
const HamburgerDrawer = ({ drawerRef, market, onClose, onItemClick }) => {

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus close button on mount
  useEffect(() => {
    if (drawerRef.current) {
      const closeBtn = drawerRef.current.querySelector('.hm-close-btn');
      if (closeBtn) closeBtn.focus();
    }
  }, [drawerRef]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="hm-backdrop" onClick={onClose}>
      <nav
        ref={drawerRef}
        className="hm-drawer"
        id="hm-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header */}
        <div className="hm-drawer-header">
          <span className="hm-drawer-title">Menu</span>
          <button
            className="hm-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            &times;
          </button>
        </div>

        {/* Menu items */}
        <div className="hm-menu-items">
          {MENU_ITEMS.map((item, index) => (
            <React.Fragment key={item.id}>
              <button
                className="hm-menu-item"
                onClick={() => onItemClick(item)}
              >
                <span className="hm-item-icon">{item.icon}</span>
                <div className="hm-item-text">
                  <span className="hm-item-title">
                    {item.title}
                  </span>
                  <span className="hm-item-subtitle">{item.subtitle}</span>
                </div>
                <span className="hm-item-arrow">&#8250;</span>
              </button>

              {/* Divider after Claim Your Venue (separates venue actions from business actions) */}
              {item.id === 'claim-venue' && <div className="hm-divider" />}
            </React.Fragment>
          ))}
        </div>

        {/* Drawer footer */}
        <div className="hm-drawer-footer">
          <button className="hm-back-to-site" onClick={onClose}>
            ← Back to Site
          </button>
        </div>
      </nav>
    </div>
  );
};

export default HamburgerMenu;
